import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  signal,
} from '@angular/core';
import { PAPER_LOCALE } from '@ricoh-paper/angular';
import {
  NavigationEnd,
  Router,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import { filter } from 'rxjs/operators';

import { AnalyticsService } from './core/analytics.service';
import { RDX_VISITORS_ENV } from './core/env';
import { VisitorsErrorHandler } from './core/error-handler';
import { paperShellProviders } from './shell/paper-shell.providers';
import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // Global error capture + user-facing toast (PostHog when keyed; always logs).
    { provide: ErrorHandler, useClass: VisitorsErrorHandler },
    // Pin every Paper date/time control to UK formatting (dd/MM/yyyy). The
    // token defaults to the BROWSER locale, so an en-US browser rendered the
    // date pickers month-first — unreadable next to the rest of the app.
    { provide: PAPER_LOCALE, useValue: signal('en-GB').asReadonly() },
    // Paper's workspace shell: theme memory, accent, rail fold, ⌘K recents,
    // the panel width and surface, and the location scope adapter.
    ...paperShellProviders(RDX_VISITORS_ENV.PREVIEW_SESSION),
    // Initialise PostHog (no-op without a key) and fire an SPA pageview on every
    // settled navigation — Angular routing never reloads the page, so PostHog's
    // own auto-pageview would miss in-app navigation.
    provideAppInitializer(() => {
      // Guest links contain credentials: no staff telemetry or session capture on this surface.
      if (window.location.pathname.startsWith('/v/')) return;
      const analytics = inject(AnalyticsService);
      analytics.init();
      const router = inject(Router);
      router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((e) => {
        const path = (e as NavigationEnd).urlAfterRedirects;
        if (!path.startsWith('/v/')) analytics.pageview(path);
      });
    }),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' }),
      // Bind route params to component inputs.
      withComponentInputBinding(),
    ),
  ],
};
